import React, { useState } from 'react';
import axios from 'axios';
import { Package, Scan, Plus, Trash2, Send, Printer, QrCode } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const WarehouseSTN = () => {
  const [sourceType, setSourceType] = useState('new_stock');
  const [scannedDID, setScannedDID] = useState('');
  const [devices, setDevices] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [createdSTN, setCreatedSTN] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleScanDID = async () => {
    if (!scannedDID.trim()) {
      alert('Please enter DID');
      return;
    }

    // Mock device lookup - in production, this would fetch from products DB
    const mockDevice = {
      did: scannedDID,
      sku: `SKU-${scannedDID}`,
      zupc: `ZUPC-${scannedDID}`,
      brand: 'Apple',
      model: 'iPhone 13',
      category: 'Mobile',
      condition_notes: '',
      customer_complaint: sourceType === 'customer_return' ? 'Screen not working' : ''
    };

    setDevices([...devices, mockDevice]);
    setScannedDID('');
  };

  const removeDevice = (index) => {
    setDevices(devices.filter((_, i) => i !== index));
  };

  const handleCreateSTN = async () => {
    if (devices.length === 0) {
      alert('Please scan at least one device');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/trc/stn/create`,
        {
          source: 'Warehouse',
          destination: 'TRC',
          source_type: sourceType,
          devices: devices,
          remarks: remarks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCreatedSTN(response.data);
        alert(`STN Created: ${response.data.stn_id}`);
      }
    } catch (error) {
      console.error('Error creating STN:', error);
      alert('Error creating STN');
    } finally {
      setLoading(false);
    }
  };

  const handleDispatch = async () => {
    if (!createdSTN) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/trc/stn/${createdSTN.stn_id}/dispatch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('STN Dispatched Successfully!');
        // Reset form
        setDevices([]);
        setCreatedSTN(null);
        setRemarks('');
      }
    } catch (error) {
      console.error('Error dispatching STN:', error);
      alert('Error dispatching STN');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Package className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Warehouse - Create STN</h1>
        </div>

        {!createdSTN ? (
          <div className="space-y-6">
            {/* Source Type Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Source Type *</label>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-lg text-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="new_stock">New Stock</option>
                <option value="customer_return">Customer Return</option>
                <option value="store_return">Store Return (Faulty)</option>
              </select>
            </div>

            {/* Scan DID */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Scan Device ID (DID)</label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={scannedDID}
                  onChange={(e) => setScannedDID(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleScanDID()}
                  placeholder="Scan or enter DID..."
                  className="flex-1 px-4 py-3 border-2 rounded-lg text-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleScanDID}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 flex items-center gap-2"
                >
                  <Scan className="w-5 h-5" />
                  Add
                </button>
              </div>
            </div>

            {/* Scanned Devices List */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Scanned Devices</h2>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                  {devices.length} devices
                </span>
              </div>

              {devices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                  <p>No devices scanned yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {devices.map((device, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border-2 rounded-lg">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900">{device.did}</div>
                        <div className="text-sm text-gray-600">
                          {device.brand} {device.model} | {device.category}
                        </div>
                        <div className="text-xs text-gray-500">SKU: {device.sku}</div>
                      </div>
                      <button
                        onClick={() => removeDevice(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Remarks */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Remarks (Optional)</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows="3"
                placeholder="Any additional notes..."
                className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Create STN Button */}
            <button
              onClick={handleCreateSTN}
              disabled={devices.length === 0 || loading}
              className="w-full py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
            >
              <Plus className="w-6 h-6" />
              {loading ? 'Creating STN...' : 'Create STN'}
            </button>
          </div>
        ) : (
          /* STN Created - Show QR and Dispatch */
          <div className="bg-white rounded-lg shadow p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <QrCode className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">STN Created Successfully!</h2>
              <p className="text-4xl font-bold text-blue-600 mb-4">{createdSTN.stn_id}</p>
              <p className="text-gray-600">Total Devices: {createdSTN.total_devices}</p>
            </div>

            <div className="border-t pt-6 space-y-4">
              <button
                onClick={() => window.print()}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Printer className="w-5 h-5" />
                Print STN & QR Code
              </button>

              <button
                onClick={handleDispatch}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Mark as Dispatched
              </button>

              <button
                onClick={() => {
                  setCreatedSTN(null);
                  setDevices([]);
                }}
                className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Create Another STN
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseSTN;
